/**
 * **One post, on its own page** — `/site/blog/:postId` and `/site/blog/new`
 * (Wave 14D; C13, spec §10.5 item 6; ADR-0032 §5; T-4.64).
 *
 * The post was an inline card on the list until Wave 14. It is now an edit page
 * on interface I-1: `EditPage` for the shell, `EditBar` for the one Save (with
 * Discard and *See it in your draft*), `useLeaveGuard` so an unsaved post is not
 * lost to a stray click, and **Delete** behind a `ConfirmDialog`.
 *
 * The draft link for a saved post goes straight to the post inside the draft
 * preview (`/_preview/<token>/blog/<slug>`), which reads drafts as well as
 * published posts — the server's `postSlugFor` does the rest. A post not yet
 * saved has no address, so the link is the site's draft home.
 *
 * `PostEditor` is the whole screen given its data and its three calls, so the
 * tests drive it without a network; `SiteBlogPost` is the route that reads.
 */

import { Alert, Badge, Button, Card, CardBody, ConfirmDialog, Field, Input, Skeleton, Textarea } from '@fiveox/ui'
import { Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'

import { EditBar, EditPage, useDraftPreviewHref, useLeaveGuard } from '../../components/edit'
import { SITE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { EMPTY_RICH_TEXT, richTextToPlain, site, type Post, type PostStatus, type PostTopic, type RichText } from '../../lib/api/site'
import { STATUS_LABEL } from './Blog'
import { ImageField } from './fields/ImageField'
import { RichTextField } from './fields/RichTextField'

export const STATUS_NOTE: Record<Exclude<PostStatus, 'archived'>, string> = {
  draft: 'Only you can see it — in your draft preview, never on your site.',
  published: 'On your site’s blog as soon as your site is published.',
}

export interface PostValues {
  title: string
  summary: string
  author: string
  published_on: string
  status: Exclude<PostStatus, 'archived'>
  body: RichText
  cover_image_url: string
  /** C24 item 15 — a post may carry **several** topics (ADR-0050 §1): a post
   *  about a GST notice raised in an audit is both, and forcing a choice would
   *  make the blog's filter lie. */
  topic_ids: string[]
}

export type PostBody = Partial<Post> & { title: string; topic_ids?: string[] }

export function postValues(post: Post | null): PostValues {
  return {
    title: post?.title ?? '',
    summary: post?.summary ?? '',
    author: post?.author ?? '',
    published_on: post?.published_on ?? '',
    status: post?.status === 'published' ? 'published' : 'draft',
    body: post?.body ?? EMPTY_RICH_TEXT,
    cover_image_url: post?.cover_image_url ?? '',
    // Absent `topics` means *not read* rather than *none* (ADR-0050 §1), and
    // an unread post must not be saved with its topics cleared — so this is
    // only ever `[]` for a post the caller did read.
    topic_ids: (post?.topics ?? []).map((t) => t.id),
  }
}

/** What Save sends — empty strings become `null`, as the service stores them. */
export function postBody(values: PostValues): PostBody {
  return {
    title: values.title.trim(),
    summary: values.summary.trim() || null,
    author: values.author.trim() || null,
    published_on: values.published_on || null,
    status: values.status,
    body: values.body,
    cover_image_url: values.cover_image_url.trim() || null,
    topic_ids: values.topic_ids,
  }
}

export function SiteBlogPost() {
  const { postId = 'new' } = useParams()
  const { can } = useEvaluation()
  const navigate = useNavigate()
  const previewBase = useDraftPreviewHref()
  const isNew = postId === 'new'

  const [post, setPost] = useState<Post | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'failed'>(isNew ? 'ready' : 'loading')
  // The post a first save just made: its new address needs no second read.
  const created = useRef<Post | null>(null)

  useEffect(() => {
    if (isNew) {
      setPost(null)
      setState('ready')
      return
    }
    if (created.current?.id === postId) {
      setPost(created.current)
      setState('ready')
      created.current = null
      return
    }
    let live = true
    setState('loading')
    site
      .post(postId)
      .then((body) => {
        if (!live) return
        setPost(body.data)
        setState('ready')
      })
      .catch((err: unknown) => {
        if (!live) return
        setState(err instanceof ApiError && err.status === 404 ? 'missing' : 'failed')
      })
    return () => {
      live = false
    }
  }, [postId, isNew])

  const back = { to: '/site/blog', label: 'Blog' }

  if (state === 'loading') {
    return (
      <EditPage title="Loading the post…" back={back}>
        <Skeleton className="h-64 rounded-xl" />
      </EditPage>
    )
  }
  if (state !== 'ready') {
    return (
      <EditPage title="Post" back={back}>
        <Alert tone="error">
          {state === 'missing'
            ? 'This post does not exist any more — it may have been deleted.'
            : 'This post could not be loaded. Reload the page to try again.'}{' '}
          <Link to="/site/blog" className="font-medium underline">
            Back to the blog
          </Link>
        </Alert>
      </EditPage>
    )
  }

  return (
    <PostEditor
      key={post?.id ?? 'new'}
      post={post}
      editable={isNew ? can(SITE, 'can_create') : can(SITE, 'can_update')}
      previewBase={previewBase}
      onCreate={async (body) => (await site.createPost(body)).data}
      onUpdate={async (id, body) => (await site.updatePost(id, body)).data}
      onDelete={async (id) => {
        await site.deletePost(id)
      }}
      onCreated={(made) => {
        created.current = made
        navigate(`/site/blog/${made.id}`, { replace: true })
      }}
      onDeleted={() => navigate('/site/blog')}
    />
  )
}

/**
 * **The topics a post is filed under** — C24 item 15, ADR-0050 §1.
 *
 * Checkboxes and not a select, because a post may carry **several** and a
 * multiple-select is a control people get wrong. The list is the firm's own,
 * read once per editor; a firm with none is told where to make one rather
 * than shown an empty box.
 */
export function TopicPicker({
  chosen,
  editable,
  onChange,
}: {
  chosen: string[]
  editable: boolean
  onChange: (next: string[]) => void
}) {
  const [topics, setTopics] = useState<PostTopic[] | null>(null)

  useEffect(() => {
    let cancelled = false
    void site
      .topics()
      .then((body) => !cancelled && setTopics(body.data))
      // A failed read costs the control and nothing else: a post must still
      // be writable when the topic list cannot be fetched.
      .catch(() => !cancelled && setTopics([]))
    return () => {
      cancelled = true
    }
  }, [])

  if (topics === null) return <Skeleton className="h-16 w-full rounded-lg" />
  if (topics.length === 0) {
    return (
      <p className="text-muted-foreground text-xs" data-no-topics="">
        You have no topics yet. Make one on{' '}
        <Link to="/site/blog" className="text-primary underline">
          Blog
        </Link>{' '}
        and your visitors can sort your posts by it.
      </p>
    )
  }

  const toggle = (id: string) =>
    onChange(chosen.includes(id) ? chosen.filter((c) => c !== id) : [...chosen, id])

  return (
    <fieldset className="space-y-1.5" data-topic-picker="">
      <legend className="text-foreground text-sm font-medium">Topics</legend>
      <p className="text-muted-foreground text-xs">
        Your visitors can sort the blog by these. A post can have more than one.
      </p>
      <div className="flex flex-wrap gap-2 pt-1">
        {topics.map((topic) => (
          <label
            key={topic.id}
            data-topic={topic.slug}
            className={[
              'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-sm',
              chosen.includes(topic.id)
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-foreground',
              editable ? '' : 'cursor-default opacity-70',
            ].join(' ')}
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={chosen.includes(topic.id)}
              disabled={!editable}
              onChange={() => toggle(topic.id)}
            />
            {topic.name}
          </label>
        ))}
      </div>
    </fieldset>
  )
}

export function PostEditor({
  post,
  editable,
  previewBase,
  onCreate,
  onUpdate,
  onDelete,
  onCreated,
  onDeleted,
}: {
  /** `null` for a new post. */
  post: Post | null
  editable: boolean
  /** The draft preview's address (`useDraftPreviewHref`), or `null`. */
  previewBase: string | null
  onCreate: (body: PostBody) => Promise<Post>
  onUpdate: (id: string, body: PostBody) => Promise<Post>
  onDelete: (id: string) => Promise<void>
  /** After the first save of a new post — the route moves to its address. */
  onCreated: (post: Post) => void
  /** After a delete — the route goes back to the list. */
  onDeleted: () => void
}) {
  const [saved, setSaved] = useState<Post | null>(post)
  const [values, setValues] = useState<PostValues>(() => postValues(post))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // A navigation that must happen *after* the render in which the page is no
  // longer dirty — otherwise the leave guard, still holding the old answer,
  // stops the page's own redirect and asks the firm about a post it just saved.
  const [then, setThen] = useState<{ kind: 'created'; post: Post } | { kind: 'deleted' } | null>(null)

  const baseline = useMemo(() => JSON.stringify(postBody(postValues(saved))), [saved])
  const dirty = editable && then === null && JSON.stringify(postBody(values)) !== baseline
  const guard = useLeaveGuard(dirty)

  // The callbacks are the parent's fresh closures on every render; held in a
  // ref so the navigation runs once per `then`, not once per render after it.
  const after = useRef({ onCreated, onDeleted })
  after.current = { onCreated, onDeleted }
  useEffect(() => {
    if (!then) return
    if (then.kind === 'created') after.current.onCreated(then.post)
    else after.current.onDeleted()
  }, [then])

  const problem = !values.title.trim() ? 'Give the post a title before saving.' : null
  const plain = richTextToPlain(values.body)
  const set = <K extends keyof PostValues>(key: K, value: PostValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }))

  const previewHref = previewBase
    ? saved
      ? `${previewBase.replace(/\/+$/, '')}/blog/${saved.slug}`
      : previewBase
    : null

  async function save() {
    if (problem) {
      // Only reachable on an untouched new post, whose bar offers Save before
      // the problem line shows (the problem is shown once something is typed).
      setError(problem)
      return
    }
    setSaving(true)
    setError(null)
    try {
      const body = postBody(values)
      if (saved) {
        const next = await onUpdate(saved.id, body)
        setSaved(next)
        setValues(postValues(next))
      } else {
        const created = await onCreate(body)
        setSaved(created)
        setValues(postValues(created))
        setThen({ kind: 'created', post: created })
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That post could not be saved. Try again.')
    } finally {
      setSaving(false)
    }
  }

  async function remove() {
    if (!saved) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await onDelete(saved.id)
      setConfirming(false)
      setThen({ kind: 'deleted' })
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : 'That post could not be deleted. Try again.')
    } finally {
      setDeleting(false)
    }
  }

  const status = saved?.status === 'published' ? 'published' : 'draft'

  return (
    <EditPage
      title={saved ? saved.title : 'New post'}
      description={
        saved
          ? `On your site at ${saved.href}`
          : 'Write it, save it as a draft, and publish it when it is ready.'
      }
      back={{ to: '/site/blog', label: 'Blog' }}
      meta={saved ? <Badge tone={status === 'published' ? 'success' : 'neutral'}>{STATUS_LABEL[status]}</Badge> : undefined}
      actions={
        saved && editable ? (
          <Button variant="ghost" onClick={() => setConfirming(true)} data-delete-post="">
            <Trash2 size={15} aria-hidden /> Delete
          </Button>
        ) : undefined
      }
    >
      {guard}
      {error && <Alert tone="error">{error}</Alert>}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="min-w-0 lg:col-span-2">
          <CardBody className="space-y-4">
            <div>
              <h2 className="text-foreground text-base font-semibold">The post</h2>
              <p className="text-muted-foreground text-sm">What your visitors read.</p>
            </div>
            <Field label="Title">
              {(control) => (
                <Input
                  {...control}
                  value={values.title}
                  disabled={!editable}
                  placeholder="New TDS rates from 1 October"
                  onChange={(e) => set('title', e.target.value)}
                />
              )}
            </Field>
            <Field label="Summary" hint="One or two sentences, shown in the list of posts and to search engines.">
              {(control) => (
                <Textarea
                  {...control}
                  rows={2}
                  value={values.summary}
                  disabled={!editable}
                  onChange={(e) => set('summary', e.target.value)}
                />
              )}
            </Field>
            <Field label="Body" labelAside={<span className="text-muted-foreground text-xs">{plain.length} characters</span>}>
              <RichTextField value={values.body} onChange={(body) => set('body', body)} />
            </Field>
          </CardBody>
        </Card>

        <div className="min-w-0 space-y-6">
          <Card>
            <CardBody className="space-y-4">
              <div>
                <h2 className="text-foreground text-base font-semibold">Status</h2>
                <p className="text-muted-foreground text-sm">Who can see this post.</p>
              </div>
              <div role="radiogroup" aria-label="Status" className="grid gap-2">
                {(['draft', 'published'] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={values.status === value}
                    disabled={!editable}
                    data-status={value}
                    onClick={() => set('status', value)}
                    className={`rounded-xl border p-3 text-left transition-colors ${
                      values.status === value
                        ? 'border-primary bg-primary/5 ring-primary ring-1'
                        : 'border-border hover:border-primary/40'
                    }`}
                  >
                    <span className="text-foreground block text-sm font-medium">{STATUS_LABEL[value]}</span>
                    <span className="text-muted-foreground block text-xs">{STATUS_NOTE[value]}</span>
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="space-y-4">
              <div>
                <h2 className="text-foreground text-base font-semibold">Details</h2>
                <p className="text-muted-foreground text-sm">Shown with the post.</p>
              </div>
              <Field label="Date" hint="The date shown on the post.">
                {(control) => (
                  <Input
                    {...control}
                    type="date"
                    value={values.published_on}
                    disabled={!editable}
                    onChange={(e) => set('published_on', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Author" hint="Optional — a partner’s name, or the firm’s.">
                {(control) => (
                  <Input
                    {...control}
                    value={values.author}
                    disabled={!editable}
                    onChange={(e) => set('author', e.target.value)}
                  />
                )}
              </Field>
              {/* C24 item 15 — which topics file this post (ADR-0050 §1). */}
              <TopicPicker
                chosen={values.topic_ids}
                editable={editable}
                onChange={(next) => set('topic_ids', next)}
              />
              {/* FR-I13 — upload, choose from the library, or paste a URL. */}
              <ImageField
                label="Cover picture"
                hint="Shown above the post and in the list of posts."
                value={values.cover_image_url}
                disabled={!editable}
                onChange={(url) => set('cover_image_url', url)}
              />
            </CardBody>
          </Card>
        </div>
      </div>

      <EditBar
        dirty={dirty}
        saving={saving}
        onSave={() => void save()}
        onDiscard={() => {
          setValues(postValues(saved))
          setError(null)
        }}
        problem={dirty ? problem : null}
        previewHref={previewHref}
        saveLabel={saved ? 'Save' : 'Save post'}
        editable={editable}
        isNew={!saved && then === null}
      />

      <ConfirmDialog
        open={confirming}
        onClose={() => {
          setConfirming(false)
          setDeleteError(null)
        }}
        title="Delete this post?"
        tone="danger"
        confirmLabel="Delete post"
        cancelLabel="Keep it"
        busy={deleting}
        busyLabel="Deleting…"
        error={deleteError}
        onConfirm={() => void remove()}
      >
        <p>
          <strong className="text-foreground font-medium">{saved?.title}</strong> comes off your website at once and
          leaves this list. Its address becomes free for another post.
        </p>
        {dirty && <p>Your unsaved changes to it go too.</p>}
      </ConfirmDialog>
    </EditPage>
  )
}
